import { AgGridModule } from '@ag-grid-community/angular';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ExcelFactoryMode, ModuleRegistry } from '@ag-grid-community/core';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { RichSelectModule } from '@ag-grid-enterprise/rich-select';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { accountRoutes } from './account.routes';

import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { TabViewModule } from 'primeng/tabview';

import { AccountEntityManagerProvider, AccountUnitOfWork } from '@data';

import { AccountAdminFrmComponent } from './account-administrator/account-administrator-frm.component';
import { AccountAdminListComponent } from './account-administrator/account-administrator-list.component';

import { AccountFrmComponent } from './account-frm.component';
import { AccountListComponent } from './account-list.component';
import { AccountManagerComponent } from './account-manager.component';
import { AccountManagerLockedComponent } from './account-manager-locked.component';

import { AccountUserFinderDialog } from './account-users/account-user-finder.dialog';
import { AccountUserFrmComponent } from './account-users/account-user-frm.component';
import { AccountUserListComponent } from './account-users/account-user-list.component';
import { ProgramUserGroupsComponent } from './account-users/program-user-groups.component';
import { ProgramFrmComponent } from './programs/program-frm.component';
import { ProgramListComponent } from './programs/program-list.component';

import { ProductTypeConfigFinderDialog } from './programs/product-type-config.finder.dialog';
import { ChangeProductTypeConfigDialog } from './programs/change-product-type-config.dialog';
import { AccountDbQueryService } from './services/account-db-query.service';
import { AccountDbSaveService } from './services/account-db-save.service';

import { AccountAddressFrmComponent } from './addresses/account-address-frm.component';
import { BudgetItemsListComponent } from './budgets/budget-items-list.component';
import { PriceListsComponent } from './price-lists/price-lists.component';

import { BreadcrumbService, CoreModule, ProximityWizardModule, TemplatesService, ProxDialogServiceProvider, AiButtonComponent } from '@core';
import { AccountAdminFinderDialog } from './account-administrator/account-admin-finder.dialog';
import { AccountAdminGroupFinderDialog } from './account-administrator/account-admin-group-finder.dialog';
import { AccountAdminGroupsComponent } from './account-administrator/account-admin-groups.component';
import { ShippingUserGroupFinderDialog } from './account-users/shipping-user-group-finder.dialog';
import { ShippingUserGroupsComponent } from './account-users/shipping-user-groups.component';
import { ActionFrmComponent } from './actions/action-frm.component';
import { ActionListComponent } from './actions/action-list.component';
import { AccountAddressFinderDialog } from './addresses/account-address-finder.dialog';
import { BillingAccountAddressesComponent } from './addresses/billing-account-addresses.component';
import { ShippingAccountAddressesComponent } from './addresses/shipping-account-addresses.component';
import { SupplierSubmissionStatusComponent } from './supplier-submission-status.component';
import { ApprovalTreesComponent } from './approval-trees/approval-trees.component';
import { AccountBudgetsComponent } from './budgets/account-budgets.component';
import { ImportUsersComponent } from './import-users/import-users.component';
import { HowToImportUsersDocComponent } from './documentation/how-to-import-users-doc.component';
import { MarkdownModule } from 'ngx-markdown';
import { AccountNotificationSettingsComponent } from './notifiications/account-notification-settings.component';
import { AccountNotificationTemplateListComponent } from './notifiications/account-notification-template-list.component';
import { NotificationTemplateDialogComponent } from './notifiications/notification-template-dialog.component';
import { TemplatePreviewDialogComponent } from './notifiications/template-preview-dialog.component';
import { PaymentProfileDialogComponent, PaymentSelectDialogComponent, PaymentService, PaymentViewDialogComponent } from './payments';
import { BudgetProductTypeTagsComponent } from './product-type-tags/budget-product-type-tags.component';
import { ConsolidateTagsDialogComponent } from './product-type-tags/consolidate-tags.dialog.component';
import { ProgramProductTypeTagsComponent } from './product-type-tags/program-product-type-tags.component.';
import { ProgramAllowancesSubComponent } from './programs/program-allowances.sub.component';
import { ProgramBillingSubComponent } from './programs/program-billing.sub.component';
import { ProgramPugTransferSubComponent } from './programs/program-pug-transfer.sub.component';
import { ProgramShippingTransferSubComponent } from './programs/program-shipping-transfer.sub.component';
// The only PrimeNG module CoreModule does not already re-export that this lib needs. Nothing
// else in the application uses drag and drop, so it has never been pulled in before.
import { DragDropModule } from 'primeng/dragdrop';
import { ProgramBudgetsSubComponent } from './programs/program-budgets.sub.component';
import { ProgramGuidelinesSubComponent } from './programs/program-guidelines.sub.component';
import { AccountGuidelinesComponent } from './guidelines/account-guidelines.component';
import { ProgramCheckSubComponent } from './programs/program-check.sub.component';
import { ProgramChangeStatusFrmComponent } from './programs/program-change-status-frm.component';
import { ProgramInfoSubComponent } from './programs/program-info.sub.component';
import { ProgramIssuancesSubComponent } from './programs/program-issuances.sub.component';
import { ProgramProductTypeConfigsSubComponent } from './programs/program-product-type-configs.sub.component';
// Throwaway: one-shot Program Product Reconcile feature. Remove this import + the
// matching entry in declarations when the cleanup work is finished.
import { ProgramReconcileSubComponent } from './programs/program-reconcile.sub.component';
import { ProgramProductTypePickerDialogComponent } from './programs/program-product-type-picker.dialog.component';
import { ProgramProductTypeTagFinderDialog } from './programs/program-product-type-tag-finder.dialog';
import { ProgramProductTypeTagsSubComponent } from './programs/program-product-type-tags.sub.component';
import { ProgramRapidTemplatesSubComponent } from './programs/program-rapid-templates.sub.component';
import { ProgramSuppliersSubComponent } from './programs/program-suppliers.sub.component';
import { ProgramUserGroupsSubComponent } from './programs/program-user-groups.sub.component';
import { ProgramViewsSubComponent } from './programs/program-views.sub.component';
import { AccountPurchaseOrdersComponent } from './purchase-orders/account-purchase-orders.component';
import { AccountProcurementCardsComponent } from './purchase-orders/account-procurement-cards.component';
import { AccountImageHandlerComponent } from './services/account-image-handler.component';
import { ProgramStatusService } from './services/program-status.service';
import { ProgramRulesService } from './services/program-rules.service';
import { OrgTreeDesignationService } from './services/org-tree-designation.service';
import { AccountRoleListComponent } from './account-administrator/account-role-list.component';
import { AccountActivateDialogComponent } from './account-activate.dialog.component';
import { AccountActivationService } from './services/account-activation.service';

import { NotificationSubmissionStatusComponent } from './notification-submission-status.component';
import { NotificationTemplateFinderDialog } from './notifiications/notification-template-finder.dialog';
import { ProgramKitsSubComponent } from './programs/program-kits.sub.component';
import { ProgramKitInfoSubComponent } from './programs/program-kit-info.sub.component';
import { ProgramKitTagsComponent } from './kit-tags/program-kit-tags.component';
import { KitFinderDialog } from './programs/program-kit-finder.dialog';
import { ProgramKitTagPickerDialogComponent } from './programs/program-kit-tag-picker.dialog.component';
import { AccountPurchaseOrderFinderDialog } from './purchase-orders/account-purchase-order-finder.dialog';
import { PurchaseOrderAllocateDialogComponent } from './purchase-orders/purchase-order-allocate.dialog.component';
import { ProgramNewChoicesDialogComponent } from './programs/program-new-choices.dialog.component';
import { ConsolidateMeSummaryDialogComponent } from './programs/consolidate-me-summary.dialog.component';
import { ProgramTestMenuDialogComponent } from './programs/program-test-menu-dialog.component';
import { ApprovalTreeUserGroupDialogComponent } from './approval-trees/approval-tree-user-group.dialog.component';
import { ApprovalTreeDialogComponent } from './approval-trees/approval-tree.dialog.component';
import { ProgramCycleDialogComponent } from './programs/program-cycle.dialog.component';
import { ProgramChangeEndDateDialogComponent } from './programs/program-change-end-date.dialog.component';
import { ProximityUser } from '@models';
import { ProximityUserNotesDialogComponent } from './proximity-user-notes-dialog';
import { ProximityUserNoteDialogComponent } from './proximity-user-note-dialog';
import { AccountUserProgramsDialogComponent } from './account-users/account-user-programs-dialog.component';
import { AccountPurchaseOrderLogFinderDialog } from './purchase-orders/account-purchase-order-log-finder.dialog';

@NgModule({
  imports: [
    CommonModule,
    CoreModule,
    AgGridModule,
    ProximityWizardModule,
    TabViewModule,
    FileUploadModule,
    MarkdownModule.forChild(),
    DragDropModule,
    RouterModule.forChild(accountRoutes),
    AiButtonComponent,
  ],
  declarations: [
    AccountAddressFrmComponent,
    AccountAddressFinderDialog,
    AccountBudgetsComponent,
    AccountListComponent,
    AccountFrmComponent,
    AccountManagerComponent,
    AccountManagerLockedComponent,
    AccountAdminListComponent,
    AccountAdminGroupsComponent,
    AccountAdminFrmComponent,
    AccountAdminFinderDialog,
    AccountAdminGroupFinderDialog,
    AccountRoleListComponent,
    AccountUserListComponent,
    AccountUserFrmComponent,
    AccountUserFinderDialog,
    ProgramProductTypeTagsComponent,
    ConsolidateTagsDialogComponent,
    BudgetProductTypeTagsComponent,
    ProgramUserGroupsComponent,
    ShippingUserGroupsComponent,
    ShippingUserGroupFinderDialog,
    ShippingAccountAddressesComponent,
    BillingAccountAddressesComponent,
    ProgramProductTypeTagFinderDialog,
    ProgramKitTagPickerDialogComponent,
    ApprovalTreesComponent,
    PriceListsComponent,
    BudgetItemsListComponent,
    AccountPurchaseOrdersComponent,
    AccountPurchaseOrderFinderDialog,
    AccountPurchaseOrderLogFinderDialog,
    AccountProcurementCardsComponent,
    AccountImageHandlerComponent,
    KitFinderDialog,

    PaymentProfileDialogComponent,
    PaymentSelectDialogComponent,
    PaymentViewDialogComponent,

    ProgramListComponent,

    ProgramFrmComponent,
    ProgramInfoSubComponent,
    ProgramUserGroupsSubComponent,
    ProgramProductTypeConfigsSubComponent,
    ProgramReconcileSubComponent,
    ProgramProductTypeTagsSubComponent,
    ProgramViewsSubComponent,
    ProgramIssuancesSubComponent,
    ProgramAllowancesSubComponent,
    ProgramRapidTemplatesSubComponent,
    ProgramBudgetsSubComponent,
    ProgramGuidelinesSubComponent,
    AccountGuidelinesComponent,
    ProgramCheckSubComponent,
    ProgramBillingSubComponent,
    ProgramPugTransferSubComponent,
    ProgramShippingTransferSubComponent,
    ProgramSuppliersSubComponent,

    ProductTypeConfigFinderDialog,
    ChangeProductTypeConfigDialog,

    ActionListComponent,
    ActionFrmComponent,
    ProgramChangeStatusFrmComponent,
    ImportUsersComponent,
    HowToImportUsersDocComponent,
    AccountNotificationTemplateListComponent,
    AccountNotificationSettingsComponent,
    NotificationTemplateDialogComponent,
    NotificationTemplateFinderDialog,
    TemplatePreviewDialogComponent,

    SupplierSubmissionStatusComponent,
    NotificationSubmissionStatusComponent,
    ProgramProductTypePickerDialogComponent,
    AccountActivateDialogComponent,
    AccountActivateDialogComponent,
    ProgramKitsSubComponent,
    ProgramKitInfoSubComponent,
    ProgramKitTagsComponent,
    PurchaseOrderAllocateDialogComponent,
    ProgramNewChoicesDialogComponent,
    ConsolidateMeSummaryDialogComponent,
    ProgramTestMenuDialogComponent,
    ApprovalTreeUserGroupDialogComponent,
    ApprovalTreeDialogComponent,
    ProgramCycleDialogComponent,
    ProgramChangeEndDateDialogComponent,
    ProximityUserNotesDialogComponent,
    ProximityUserNoteDialogComponent,
    AccountUserProgramsDialogComponent,
  ],
  exports: [
    PaymentProfileDialogComponent,
    PaymentSelectDialogComponent,
    PaymentViewDialogComponent,
    ProximityUserNotesDialogComponent,
    ProximityUserNoteDialogComponent,
  ],
  providers: [
    ProxDialogServiceProvider,
    ConfirmationService,
    BreadcrumbService,
    AccountDbQueryService,
    AccountDbSaveService,
    AccountEntityManagerProvider,
    AccountUnitOfWork,
    MessageService,
    TemplatesService,
    ProgramStatusService,
    PaymentService,
    ProgramRulesService,
    AccountActivationService,
    // Must be listed here, not left to providedIn: 'root'. CoreModule provides
    // ConfirmationService / DialogService / UtilDialogService as MODULE providers, so this lazy
    // module has its own instances. A root-scoped service resolves the ROOT UtilDialogService
    // instead, calls confirm() on a ConfirmationService nobody is subscribed to, and its dialogs
    // silently never render. Same split makes a root-scoped save service throw
    // "Only entities in this entityManager may be saved".
    OrgTreeDesignationService,
  ],
})
export class AccountModule {
  constructor() {
    ModuleRegistry.registerModules([
      ClientSideRowModelModule,
      ExcelExportModule,
      InfiniteRowModelModule,
      ServerSideRowModelModule,
      MasterDetailModule,
      SetFilterModule,
      RichSelectModule,
      RangeSelectionModule,
      RowGroupingModule,
      MenuModule,
      // The Analyze dialog (prox-analysis-grid-dialog) needs the side bar and both tool panels.
      // ModuleRegistry is global, so these appeared to work before only when AccountAdminModule
      // happened to load first. That is a load-order accident, so register them here too.
      SideBarModule,
      ColumnsToolPanelModule,
      FiltersToolPanelModule,
    ]);
  }
}
